// React
import React, { useState, useRef } from "react";

// Internal Components
import { isUrl } from "../../../../utils/UtilityFunctions";
import { URGENCY_OPTIONS } from "../Prioritisation/Urgency";
import AutoResizeTextArea from "../../../../components/ui/AutoResizeTextarea/AutosizeTextarea";

// External Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faEllipsisVertical,
  faSquareMinus,
  faTrashAlt,
  faBullseye,
} from "@fortawesome/free-solid-svg-icons";
import { faSquarePlus, faCalendar } from "@fortawesome/free-regular-svg-icons";

const ESTIMATE_OPTIONS = [
  { value: 25, label: "25m" },
  { value: 50, label: "50m" },
  { value: 75, label: "1h 15m" },
  { value: 100, label: "1h 40m" },
  { value: 125, label: "2h 5m" },
  { value: 150, label: "2h 30m" },
];

const formatDate = (date) => {
  if (!date) return null;
  const d = new Date(date);
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

const formatEstimate = (mins) => {
  if (!mins) return null;
  return mins < 60 ? `${mins}m` : `${mins / 60}h`;
};

const MOMENT_KEYS = [
  "momentOne",
  "momentTwo",
  "momentThree",
  "momentFour",
  "momentFive",
];

function Input({
  task,
  taskId,
  title,
  display,
  deleteSubtask,
  toggleSubTask,
  toggleOverlay,
  updateDescription,
  handleCalloutDisplay,
  handleUrgencyChange,
  handleEstimateChange,
  mode = "task",
  onPromoteToMoment,
  momentValues,
}) {
  const isStep = mode === "step";
  const canPromote = mode === "step" || mode === "daySubtask";
  const dateInputRef = useRef(null);
  const [showUrgencyPicker, setShowUrgencyPicker] = useState(false);
  const [showEstimatePicker, setShowEstimatePicker] = useState(false);
  const [showMomentPicker, setShowMomentPicker] = useState(false);

  const activeUrgency = URGENCY_OPTIONS.find((o) => o.value === task.urgency);

  const handleCompleteToggle = () => {
    toggleSubTask(taskId, title);
  };

  const handleUrgencyClear = (e) => {
    e.stopPropagation();
    handleUrgencyChange(
      {
        currentTarget: { dataset: { displayName: "remove", name: title } },
        target: { name: title, value: "" },
      },
      taskId,
      title,
      "urgency",
    );
    setShowUrgencyPicker(false);
  };

  const handleDeadlineClear = (e) => {
    e.stopPropagation();
    handleUrgencyChange(
      {
        currentTarget: { dataset: { displayName: "remove", name: title } },
        target: { name: title, value: "" },
      },
      taskId,
      title,
      "deadline",
    );
  };

  const isCompleted = task.completed !== 0;

  return (
    <div
      className={
        isCompleted ? "task-item-row task-item-row--done" : "task-item-row"
      }
    >
      {/* Text body + chips + selectors */}
      <div className="task-body">
        {isUrl(task.description) ? (
          <a href={task.description} target="_blank" rel="noreferrer">
            <AutoResizeTextArea
              className={
                isCompleted
                  ? "task--input task--input--on completed"
                  : "task--input task--input--on"
              }
              value={task.description}
              onChange={(event) => updateDescription(event, title, taskId)}
              readOnly
            />
          </a>
        ) : (
          <AutoResizeTextArea
            className={
              isCompleted
                ? "task--input task--input--on completed"
                : "task--input task--input--on"
            }
            value={task.description}
            onChange={(event) => updateDescription(event, title, taskId)}
            title={title}
          />
        )}

        {/* Active metadata chips */}
        {(task.urgency || task.deadline || task.estimate) && (
          <div className="task-chips">
            {task.urgency && activeUrgency && (
              <button
                type="button"
                className="task-chip task-chip--urgency"
                style={{ backgroundColor: activeUrgency.color }}
                data-display-name="remove"
                data-name={title}
                onClick={handleUrgencyClear}
              >
                {activeUrgency.label} ×
              </button>
            )}
            {task.deadline && (
              <button
                type="button"
                className="task-chip task-chip--deadline"
                onClick={() => {
                  const input = dateInputRef.current;
                  if (!input) return;
                  try {
                    input.showPicker?.();
                  } catch {
                    input.focus();
                    input.click();
                  }
                }}
              >
                {formatDate(task.deadline)}
                <span
                  className="task-chip__clear"
                  onClick={handleDeadlineClear}
                >
                  ×
                </span>
              </button>
            )}
            {task.estimate && (
              <button
                type="button"
                className="task-chip task-chip--estimate"
                onClick={() => {
                  setShowEstimatePicker((p) => !p);
                  setShowUrgencyPicker(false);
                  setShowMomentPicker(false);
                }}
              >
                ⏱ {formatEstimate(task.estimate)}
                <span
                  className="task-chip__clear"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEstimateChange(taskId, title, null);
                  }}
                >
                  ×
                </span>
              </button>
            )}
          </div>
        )}

        {/* Urgency selector — inline, opens on icon click */}
        {showUrgencyPicker && (
          <div className="task-meta-selector urgency-selector">
            {URGENCY_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={`urgency-option${
                  task.urgency === opt.value ? " urgency-option--selected" : ""
                }`}
                style={{ backgroundColor: opt.color }}
                data-value={opt.value}
                data-name={title}
                data-display-name="urgency"
                onClick={(e) => {
                  handleUrgencyChange(e, taskId, title);
                  setShowUrgencyPicker(false);
                  setShowMomentPicker(false);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Estimate selector — inline, opens on icon click */}
        {showEstimatePicker && (
          <div className="task-meta-selector estimate-selector">
            {ESTIMATE_OPTIONS.map((opt) => (
              <button
                type="button"
                key={opt.value}
                className={`estimate-option${
                  task.estimate === opt.value
                    ? " estimate-option--selected"
                    : ""
                }`}
                onClick={() => {
                  handleEstimateChange(taskId, title, opt.value);
                  setShowEstimatePicker(false);
                }}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Moment picker — available for step rows (in Callout) and for
            subtask rows on the Day screen */}
        {canPromote && showMomentPicker && (
          <div className="task-meta-selector moment-selector">
            {MOMENT_KEYS.map((key, i) => (
              <button
                type="button"
                key={key}
                className={`moment-option${
                  momentValues?.[key] ? " moment-option--filled" : ""
                }`}
                onClick={() => {
                  if (onPromoteToMoment) {
                    onPromoteToMoment(task.description, key);
                  }
                  setShowMomentPicker(false);
                }}
                title={
                  momentValues?.[key]
                    ? `Append to Moment ${i + 1}`
                    : `Set Moment ${i + 1}`
                }
              >
                Moment {i + 1}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 2×2 action icon grid */}
      <div className="task-actions">
        <div className="task-action">
          <div
            className={
              isCompleted ? "task-radio task-radio--done" : "task-radio"
            }
            onClick={handleCompleteToggle}
          />
        </div>

        {/* Slot 2: Callout (tasks) OR Delete step (steps).
            Steps have no detail view, so the trash icon takes the
            ellipsis's slot here. */}
        {isStep ? (
          <button
            type="button"
            className="task-action"
            onClick={() => {
              deleteSubtask(title, taskId);
            }}
            title="Delete step"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        ) : (
          <button
            type="button"
            className="task-action"
            data-display-name="callout"
            onClick={(e) => {
              toggleOverlay(true);
              handleCalloutDisplay(e);
            }}
            title="Open task detail"
          >
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </button>
        )}
        {/* Estimate */}
        <button
          type="button"
          className={`task-action${task.estimate ? " task-action--set" : ""}`}
          onClick={() => {
            setShowEstimatePicker((p) => !p);
            setShowUrgencyPicker(false);
            setShowMomentPicker(false);
          }}
          title="Time estimate"
        >
          <FontAwesomeIcon icon={faClock} />
        </button>

        {/* Deadline — native date input overlaid on the icon so mobile taps
            open the picker directly (showPicker() is unreliable on iOS/Android) */}
        <div
          className={`task-action task-action--deadline${task.deadline ? " task-action--set" : ""}`}
          title="Set deadline"
        >
          <FontAwesomeIcon icon={faCalendar} />
          <input
            ref={dateInputRef}
            type="date"
            className="task-date-input"
            value={task.deadline || ""}
            name={title}
            onChange={(e) => handleUrgencyChange(e, taskId, title)}
            aria-label="Set deadline"
          />
        </div>

        {/* Urgency */}
        <button
          type="button"
          className={`task-action${task.urgency ? " task-action--set" : ""}`}
          onClick={(e) => {
            if (task.urgency) {
              handleUrgencyClear(e);
            } else {
              setShowUrgencyPicker((p) => !p);
              setShowEstimatePicker(false);
              setShowMomentPicker(false);
            }
          }}
          title="Set priority"
        >
          <FontAwesomeIcon icon={task.urgency ? faSquareMinus : faSquarePlus} />
        </button>
        {/* Slot 6: Promote to moment (steps + Day-screen subtasks) OR delete.
            Day-screen subtasks lose the trash here — deletion is still
            available via the "Delete Task" button in the Callout footer. */}
        {canPromote ? (
          <button
            type="button"
            className="task-action"
            onClick={() => {
              setShowMomentPicker((p) => !p);
              setShowUrgencyPicker(false);
              setShowEstimatePicker(false);
            }}
            title={
              isStep ? "Add this step to a moment" : "Add this task to a moment"
            }
          >
            <FontAwesomeIcon icon={faBullseye} />
          </button>
        ) : (
          <button
            type="button"
            className="task-action"
            onClick={() => {
              deleteSubtask(title, taskId);
            }}
            title="Delete task"
          >
            <FontAwesomeIcon icon={faTrashAlt} />
          </button>
        )}
      </div>
    </div>
  );
}

export default Input;
